import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { ErrorStateMatcher } from '@angular/material/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Store } from '@ngxs/store'

import { DropToInspectionTypeDetail, UpdateInspectionFormData } from 'src/app/app.state'
import { MIN_NUMBER_OF_CHOICES, NAME_REGEX } from 'src/app/constants/internationalized-constants-en'
import { InspectionTypeFormData } from 'src/app/views/inspection-type/inspection-type.component'
import { InspectionRowEditDialogData } from '../inspection-type-detail/inspection-type-detail.component'

export class DropdownFieldErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl) {
    return control && (control.touched || control.dirty) && control.invalid
  }
}

export interface InspectionDetailDropdownField {
  fieldNameLabel: string
  hint: string
  select: string[]
  required: boolean
}

export interface DropdownFieldForm {
  label: string
  helper: string
  choices: any[]
  required: boolean
}

@Component({
  selector: 'app-inspection-detail-row-dropdown-edit-dialog',
  templateUrl: './inspection-detail-row-dropdown-edit-dialog.component.html',
  styleUrls: ['./inspection-detail-row-dropdown-edit-dialog.component.scss']
})
export class InspectionDetailRowDropdownEditDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('labelInput') labelInput: ElementRef

  dropdownEditForm: FormGroup

  minNumberOfChoices = MIN_NUMBER_OF_CHOICES
  private readonly mouseHoverPreviewClass = 'mouse-hover-preview'
  isMouseDragging = false

  matcher = new DropdownFieldErrorStateMatcher()

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: InspectionRowEditDialogData,
    private formBuilder: FormBuilder,
    private store: Store,
    private dialogRef: MatDialogRef<InspectionDetailRowDropdownEditDialogComponent>
  ) {
    if (this.data) {
      const formData = this.transformFormData(this.data.row)
      this.dropdownEditForm = this.formBuilder.group({
        label: new FormControl(formData.label, {
          validators: [
            Validators.required,
            Validators.pattern(NAME_REGEX),
            Validators.maxLength(20),
          ]
        }),
        helper: new FormControl(formData.helper, {
          validators: [
            Validators.required,
            Validators.pattern(NAME_REGEX),
            Validators.maxLength(40),
          ]
        }),
        choices: new FormArray([]),
        required: new FormControl(formData.required),
      })
      this.prepareChoices(formData.choices)
    }
  }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.labelInput) {
        this.labelInput.nativeElement.focus()
      }
    }, 150)
  }

  onMouseEnter(target: HTMLDivElement) {
    if (this.isMouseDragging) {
      return
    }

    if (this.keyboardGrabbedChoiceIndex !== -1) {
      // Moving to mouse interaction should release keyboard-grab ownership immediately.
      this.resetKeyboardGrabState()
    }

    this.clearChoicePreviewStyles()

    const choiceBox = target.closest('.choice-box')
    if (choiceBox && !choiceBox.classList.contains('cdk-drag-dragging')) {
      choiceBox.classList.add(this.mouseHoverPreviewClass)
    }
  }

  onMouseOut(target: HTMLDivElement) {
    if (this.isMouseDragging) {
      return
    }

    const choiceBox = target.closest('.choice-box')
    if (choiceBox) {
      choiceBox.classList.remove(this.mouseHoverPreviewClass)
    }
  }

  keyboardGrabbedChoiceIndex = -1
  keyboardGrabbedOriginalIndex = -1

  get choiceList() {
    return this.dropdownEditForm.controls.choices as FormArray
  }

  handleChoiceKeydown(event: KeyboardEvent, index: number) {
    if (this.keyboardGrabbedChoiceIndex === -1) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.clearChoicePreviewStyles()
        this.keyboardGrabbedChoiceIndex = index
        this.keyboardGrabbedOriginalIndex = index
      }
    } else {
      if (event.key === 'Tab') {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.resetKeyboardGrabState()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (this.keyboardGrabbedOriginalIndex !== -1 && this.keyboardGrabbedOriginalIndex !== this.keyboardGrabbedChoiceIndex) {
          this.moveChoice(this.keyboardGrabbedChoiceIndex, this.keyboardGrabbedOriginalIndex)
        }
        const originalIndex = this.keyboardGrabbedOriginalIndex
        this.resetKeyboardGrabState()
        this.focusHandleAsync(originalIndex)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (this.keyboardGrabbedChoiceIndex > 0) {
          const newIndex = this.keyboardGrabbedChoiceIndex - 1
          this.moveChoice(this.keyboardGrabbedChoiceIndex, newIndex)
          this.keyboardGrabbedChoiceIndex = newIndex
          this.focusHandleAsync(newIndex)
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (this.keyboardGrabbedChoiceIndex < this.choiceList.length - 1) {
          const newIndex = this.keyboardGrabbedChoiceIndex + 1
          this.moveChoice(this.keyboardGrabbedChoiceIndex, newIndex)
          this.keyboardGrabbedChoiceIndex = newIndex
          this.focusHandleAsync(newIndex)
        }
      }
    }
  }

  moveChoice(fromIndex: number, toIndex: number) {
    const control = this.choiceList.at(fromIndex)
    this.choiceList.removeAt(fromIndex)
    this.choiceList.insert(toIndex, control)
  }

  clearChoicePreviewStyles() {
    const previewChoices = document.querySelectorAll(`.choice-box.${this.mouseHoverPreviewClass}`)
    previewChoices.forEach(choice => choice.classList.remove(this.mouseHoverPreviewClass))
  }

  resetKeyboardGrabState() {
    this.keyboardGrabbedChoiceIndex = -1
    this.keyboardGrabbedOriginalIndex = -1
  }

  handleMouseGrabStart() {
    this.isMouseDragging = true
    this.clearChoicePreviewStyles()
    this.resetKeyboardGrabState()
  }

  handleMouseGrabEnd() {
    this.isMouseDragging = false
    this.clearChoicePreviewStyles()
  }

  focusHandleAsync(index: number) {
    setTimeout(() => {
      const handles = document.querySelectorAll('.drop-choice-button')
      if (handles && handles.length > index) {
        const handle = handles[index] as HTMLElement
        handle.focus()

        const choiceBox = handle.closest('.choice-box')
        if (choiceBox) {
          choiceBox.scrollIntoView({ block: 'nearest' })
        } else {
          handle.scrollIntoView({ block: 'nearest' })
        }
      }
    })
  }

  handleApply() {
    if (this.dropdownEditForm.invalid) {
      return
    }

    const formValue = this.dropdownEditForm.value
    const updateValue = {
      ...this.data.row,
      ...this.transformData(formValue),
      fieldNameLangKey: '',
      hintLangKey: '',
      selectLangKeys: [],
    }

    const action = this.data.isUpdate ? UpdateInspectionFormData : DropToInspectionTypeDetail
    this.store.dispatch(new action(
      this.data.inspectionTypeIndex,
      this.data.formDataIndex,
      updateValue,
    ))
    this.dialogRef.close()
  }

  transformFormData(val: InspectionTypeFormData): DropdownFieldForm {
    return {
      label: val.fieldNameLabel,
      helper: val.hint,
      choices: val.select,
      required: val.required,
    }
  }

  transformData(val: DropdownFieldForm): InspectionDetailDropdownField {
    return {
      fieldNameLabel: val.label,
      hint: val.helper,
      select: val.choices.map(choice => choice.name),
      required: val.required,
    }
  }

  prepareChoices(choiceList: string[]) {
    const padArray = (target: string[], targetLength: number, padValue: string): string[] => {
      const currentLength = !target ? 0 : target.length
      if (currentLength >= targetLength) {
        return target
      }
      return [
        ...(target || []),
        ...Array(targetLength - currentLength).fill(padValue)
      ]
    }

    for (const choice of padArray(choiceList, this.minNumberOfChoices, '')) {
      this.addChoice(choice)
    }
  }

  removeChoice(choiceIndex: number) {
    this.choiceList.removeAt(choiceIndex)
  }

  addChoice(choice: string = '', choiceIndex: number = null) {
    const createNewChoice = (choiceName: string) => this.formBuilder.group({
      name: new FormControl(choiceName, {
        validators: [
          Validators.required,
          Validators.pattern(NAME_REGEX),
        ]
      })
    })
    const newChoiceIndex = !choiceIndex && choiceIndex !== 0 ? choiceIndex : choiceIndex + 1

    if (newChoiceIndex) {
      this.choiceList.insert(newChoiceIndex, createNewChoice(choice))
    } else {
      this.choiceList.push(createNewChoice(choice))
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    // Ignore drop if event is invalid
    if (!event) {
      return
    }

    // Ignore drop outside of the choice list
    if (!event.isPointerOverContainer) {
      return
    }

    // Ignore drop the choice in the same position
    if (event.previousIndex === event.currentIndex) {
      return
    }

    const control = this.choiceList.at(event.previousIndex)
    this.choiceList.removeAt(event.previousIndex)
    this.choiceList.insert(event.currentIndex, control)
    this.resetKeyboardGrabState()
    this.clearChoicePreviewStyles()
  }
}
